// dependencies
import React, { useMemo, useCallback } from "react";
import { StyleSheet, css } from "aphrodite";

// libraries
import EnhancedDate from "../../libraries/EnhancedDate";

// components
import Text from "../../components/Text";

// contexts
import { useDatePicker } from "../../containers/HeadlessDatePicker/datePicker";
import { useAvailabilities } from "./availabilities";

type PropsType = {
  date: EnhancedDate;
  type: "normal" | "leading" | "trailing";
};

const styles = StyleSheet.create({
  datePickerDay: {
    display: "flex",
    flexGrow: 1,
    flexBasis: "0",
    flexShrink: 0,
    height: "2.0625rem",
    alignItems: "center",
    justifyContent: "center",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#cfdadd",
    marginRight: -1,
    marginBottom: -1,
    paddingRight: 1,
    paddingBottom: 1,
    cursor: "default",
  },
  datePickerDay__interactive: {
    cursor: "pointer",
  },
});

const backgroundColors = {
  default: "#eeeeef",
  leadingOrTrailing: "#eeeeef",
  busy: "#C76B7A",
  hover: "#1F9462",
  interaction: "#156543",
  availableWithConditions: "#D6A35C",
  availableWithoutConditions: "#82A145",
};

const textColors = {
  default: "#d4d4d4",
  leadingOrTrailing: "#ffffff",
  highlight: "#ffff",
};

/**
 * Retourne le linear gradient pour le background-image de la case.
 * Gère la mise en place d'un séparateur entre deux couleurs.
 * @param {object} param0
 * @param {object} param0.availability
 * @param {"normal" | "leading" | "trailing"} param0.type
 * @return {string}
 */
function getBackgroundImage({ availability, type }) {
  if ("normal" === type) {
    if (availability) {
      return availability.arrivalBackgroundColor !==
        availability.departureBackgroundColor
        ? `linear-gradient(to right bottom, ${
            backgroundColors[availability.departureBackgroundColor]
          } 49%, #ffffff 49%, #ffffff 51%, ${
            backgroundColors[availability.arrivalBackgroundColor]
          } 51%)`
        : `linear-gradient(to right bottom, ${
            backgroundColors[availability.departureBackgroundColor]
          } 50%, ${backgroundColors[availability.arrivalBackgroundColor]} 50%)`;
    }
  }

  return `linear-gradient(to right bottom, ${backgroundColors.default} 50%, ${backgroundColors.default} 50%)`;
}

/**
 * Retourne la couleur du texte de la case.
 * @param {object} param0
 * @param {object} param0.availability
 * @param {"normal" | "leading" | "trailing"} param0.type
 * @return {string}
 */
function getTextColor({ availability, type }) {
  if ("normal" === type && availability) {
    return textColors[availability.textColor];
  }

  return textColors.default;
}

const DatePickerDay: React.FunctionComponent<PropsType> = (props) => {
  const { date, type } = props;

  const { hover, readOnly, onChangeHover, onChangeSelect } = useDatePicker();

  const { dayStates } = useAvailabilities();

  const availability = dayStates[date.format("Y-m-d")];

  const interactive = "normal" === type && (availability?.interactive ?? false);

  const color = getTextColor({ availability, type });
  const backgroundImage = getBackgroundImage({ availability, type });

  /**
   * Gère le clic sur la date.
   */
  const handlePress = useCallback(() => {
    if (interactive) {
      onChangeSelect(date);
    }
  }, [date, interactive, onChangeSelect]);

  /**
   * Gère le l'entrée du survol dans la date.
   */
  const handleMouseEnter = useCallback(() => {
    onChangeHover(date);
  }, [date, onChangeHover]);

  /**
   * Gère la sortie du survol dans le date.
   */
  const handleMouseLeave = useCallback(() => {
    onChangeHover(null);
  }, [onChangeHover]);

  return useMemo(() => {
    return (
      <div
        onClick={handlePress}
        className={css(
          styles.datePickerDay,
          interactive && !readOnly && styles.datePickerDay__interactive
        )}
        style={{
          backgroundImage,
        }}
        {...(interactive &&
          !readOnly && {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
          })}
      >
        <Text style={{ color }}>{date.format("j")}</Text>
      </div>
    );
  }, [
    date,
    color,
    backgroundImage,
    interactive,
    readOnly,
    handleMouseEnter,
    handleMouseLeave,
    handlePress,
  ]);
};

export default DatePickerDay;
